import type { FlightScheduleChangeFareRule, FlightScheduleChangeFarePenalty } from 'api/flightScheduleChanges.types'
import { useIntl, FormattedMessage } from 'react-intl'
import FormatCurrency from 'components/Common/FormatCurrency'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'

interface Props {
  rules: Array<FlightScheduleChangeFareRule>
}

function ScheduleChangePenalty({ penalty }: { penalty: FlightScheduleChangeFarePenalty }) {
  const intl = useIntl()
  const amount = penalty.amount !== null && penalty.currency
    ? <FormatCurrency value={penalty.amount} currency={penalty.currency} />
    : undefined
  const label = penalty.applicability === 'BEFORE_DEPARTURE'
    ? intl.formatMessage({
        id: 'EC_92jDta_cPr1rO2tPX',
        defaultMessage: 'Before departure',
        description: 'Applicability label for a captured schedule change fare penalty before departure',
      })
    : penalty.applicability === 'AFTER_DEPARTURE'
      ? intl.formatMessage({
          id: '2Y_z_1_jFmcuhYLCpkBc',
          defaultMessage: 'After departure',
          description: 'Applicability label for a captured schedule change fare penalty after departure',
        })
      : undefined
  const applicability = label ?? intl.formatMessage({
    id: 'IMi773DuWKO0qtDpSBwg',
    defaultMessage: 'Timing not specified',
    description: 'Fallback when the schedule change fare penalty does not specify its applicability',
  })
  const displayedAmount = amount ?? intl.formatMessage({
    id: 'yJ-yEAC21mW-7MO-6gEp',
    defaultMessage: 'Amount not provided',
    description: 'Fallback when the schedule change fare penalty does not include a usable monetary amount',
  })
  return <BodyText variant="medium">
    {penalty.conditionsApply ? <FormattedMessage id="gbC_vvb_uIGL5WCQ2lYC" defaultMessage="{applicability}: {amount} (conditions apply)" description="Captured schedule change fare penalty including its timing, amount and conditional restrictions" values={{ applicability, amount: displayedAmount }} /> : <FormattedMessage id="71TGgPCmq1uokvSeJG-K" defaultMessage="{applicability}: {amount}" description="Captured schedule change fare penalty including its applicability and monetary amount" values={{ applicability, amount: displayedAmount }} />}
  </BodyText>
}

function ScheduleChangeFareRules({ rules }: Props) {
  const intl = useIntl()
  return <VerticalSpacer gap={20}>
    <BodyText variant="medium">{intl.formatMessage({
      id: 'XX2mx0YuGoOVJRp-mFaX',
      defaultMessage: 'These are the fare conditions captured for this schedule change. They may not include all airline restrictions.',
      description: 'Notice explaining the limits of structured fare rules captured for a schedule change',
    })}
    </BodyText>
    {rules.map((rule, index) => <VerticalSpacer gap={8} key={`${rule.originAirportCode}-${rule.destinationAirportCode}-${rule.passengerType}-${index}`}>
      {rule.originAirportCode && rule.destinationAirportCode && <Heading variant="heading6">{intl.formatMessage({
        id: 'xj_iZJdRwn-OP7QZ0loJ',
        defaultMessage: '{origin} to {destination}',
        description: 'Route identifying the captured fare rules for a schedule change booking or flight',
      }, { origin: rule.originAirportCode, destination: rule.destinationAirportCode })}
      </Heading>}
      {rule.passengerType && <BodyText variant="medium">{intl.formatMessage({
        id: '69jZAksfkoUB7F0rnYX3',
        defaultMessage: 'Passenger type: {passengerType}',
        description: 'Passenger type supplied by the airline for captured schedule change fare rules',
      }, { passengerType: rule.passengerType })}
      </BodyText>}
      {rule.isChangeable !== null && <BodyText variant="medium">{rule.isChangeable
        ? intl.formatMessage({
            id: '4aQW02N_gRszTcbWkS1V',
            defaultMessage: 'Changes permitted',
            description: 'Captured schedule change fare rule indicating changes are permitted by the airline',
          })
        : intl.formatMessage({
            id: 'PRn8uQE8vsAZOc-F5LUS',
            defaultMessage: 'Changes not permitted',
            description: 'Captured schedule change fare rule indicating changes are not permitted by the airline',
          })}
      </BodyText>}
      {rule.isRefundable !== null && <BodyText variant="medium">{rule.isRefundable
        ? intl.formatMessage({
            id: 'i6KuNSnFBDkpk5IURWK-',
            defaultMessage: 'Refunds permitted',
            description: 'Captured schedule change fare rule indicating refunds are permitted by the airline',
          })
        : intl.formatMessage({
            id: 'N9qyOx8OxuJNE8BXpEpZ',
            defaultMessage: 'Refunds not permitted',
            description: 'Captured schedule change fare rule indicating refunds are not permitted by the airline',
          })}
      </BodyText>}
      {rule.isCancelable !== null && <BodyText variant="medium">{rule.isCancelable
        ? intl.formatMessage({
            id: 'wO25hDtEVPpS_r3qQE6f',
            defaultMessage: 'Cancellation permitted',
            description: 'Captured schedule change fare rule indicating cancellation is permitted by the airline',
          })
        : intl.formatMessage({
            id: 'foyYnOWVnGLZr791bv7y',
            defaultMessage: 'Cancellation not permitted',
            description: 'Captured schedule change fare rule indicating cancellation is not permitted by the airline',
          })}
      </BodyText>}
      {rule.exchangePenalties.length > 0 && <Heading variant="heading6">{intl.formatMessage({
        id: 'fQjltkH5jmJEVNaEjukk',
        defaultMessage: 'Change fees',
        description: 'Heading for change penalties captured in the schedule change fare conditions',
      })}
      </Heading>}
      {rule.exchangePenalties.map((penalty, i) => <ScheduleChangePenalty key={i} penalty={penalty} />)}
      {rule.refundPenalties.length > 0 && <Heading variant="heading6">{intl.formatMessage({
        id: '9Kxmeo3NlvqLDTb4MBnV',
        defaultMessage: 'Refund fees',
        description: 'Heading for refund penalties captured in the schedule change fare conditions',
      })}
      </Heading>}
      {rule.refundPenalties.map((penalty, i) => <ScheduleChangePenalty key={i} penalty={penalty} />)}
    </VerticalSpacer>)}
  </VerticalSpacer>
}

export default ScheduleChangeFareRules
